import Close from '@mui/icons-material/Close';
import MenuIcon from '@mui/icons-material/Menu';
import SearchIcon from '@mui/icons-material/Search';
import {
  AppBar,
  Box,
  Container,
  IconButton,
  LinearProgress,
  styled,
  Toolbar,
  Tooltip,
} from '@mui/material';
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { AdminMenu } from '~/components/AppHeader/AdminMenu';
import { MobileMenu } from '~/components/AppHeader/MobileMenu';
import { ProductMenu } from '~/components/AppHeader/ProductMenu';
import { ProfileMenu } from '~/components/AppHeader/ProfileMenu';
import { SupportMenu } from '~/components/AppHeader/SupportMenu';
import { Button } from '~/components/Button';
import { FeedbackButton } from '~/components/FeedbackButton/FeedbackButton';
import Image from '~/components/Image';
import { Link } from '~/components/Link';
import useRoute from '~/hooks/useRoute';
import { useUser } from '~/hooks/useUser';
import { isAdmin } from '~/scripts/isAdmin';

interface AppHeaderProps {
  loading: boolean;
}
export const AppHeader = ({ loading }: AppHeaderProps) => {
  const user = useUser();
  const route = useRoute();
  const [searchParams] = useSearchParams();
  const queryParams = useMemo(() => {
    return searchParams.toString() ? `?${searchParams.toString()}` : '';
  }, [searchParams]);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const onMobileMenuToggle = () => {
    setMobileMenuOpen((open) => {
      return !open;
    });
  };

  const onMobileMenuClose = () => {
    setMobileMenuOpen(false);
  };

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{ backgroundColor: '#033d6c', maxHeight: '70px' }}>
      <Container maxWidth="xl">
        <Toolbar disableGutters sx={{ maxHeight: '70px' }}>
          <Box>
            <Link
              underline="none"
              href={user.isGuest ? '/' : '/dashboard'}
              sx={{ display: {}, mr: 1 }}>
              <Image
                height={40}
                width={147}
                sx={{
                  width: 'auto',
                  marginTop: '4px',
                }}
                src={`${
                  import.meta.env.VITE_STATIC_ASSETS_URL
                }/img/ND_Text_Web2.png`}
                alt="NerdyData.com"
              />
            </Link>
          </Box>

          <Box sx={{ flexGrow: 1 }}></Box>

          <Box
            sx={{
              display: { xs: 'none', md: 'flex' },
              alignItems: 'center',
            }}>
            <Button
              href="/technologies"
              startIcon={<SearchIcon />}
              {...navButtonProps}>
              Search
            </Button>

            <ProductMenu />

            {user.isUpgraded !== true && (
              <Button href={'/pricing' + queryParams} {...navButtonProps}>
                Pricing
              </Button>
            )}

            {user.isGuest && (
              <AuthButton path={route.path} queryParams={queryParams} />
            )}

            <SupportMenu isGuest={!!user.isGuest} />

            {!user.isGuest && <ProfileMenu />}

            {isAdmin(user) && <AdminMenu />}
          </Box>

          <Box
            sx={{
              flexGrow: 0,
              display: { xs: 'flex', md: 'none' },
              alignItems: 'center',
            }}>
            {user.isGuest ? (
              <AuthButton path={route.path} queryParams={queryParams} />
            ) : (
              <FeedbackButton iconOnly />
            )}
            <Tooltip title="Navigation Menu">
              <IconButton
                onClick={onMobileMenuToggle}
                color="inherit"
                aria-expanded={mobileMenuOpen}>
                {mobileMenuOpen ? (
                  <Close fontSize="large" />
                ) : (
                  <MenuIcon fontSize="large" />
                )}
              </IconButton>
            </Tooltip>
          </Box>
        </Toolbar>
      </Container>
      <MobileMenu
        open={mobileMenuOpen}
        onClose={onMobileMenuClose}
        queryParams={queryParams}
      />
      {loading && (
        <SlowLinearProgress
          sx={{ height: '4px', width: '100%', marginBottom: '-4px' }}
        />
      )}
    </AppBar>
  );
};

const navButtonProps = {
  variant: 'contained',
  disableElevation: true,
  sx: {
    my: 2,
    mx: 1,
    backgroundColor: '#033d6c',
  },
} as const;

interface AuthButtonProps {
  path: string;
  queryParams: string;
}

const AuthButton = ({ path, queryParams }: AuthButtonProps) => {
  if (path === '/signup') {
    return (
      <Button href={'/login' + queryParams} {...navButtonProps}>
        Log In
      </Button>
    );
  }
  return (
    <Button
      href={'/signup' + queryParams}
      variant="outlined"
      color="inherit"
      sx={{ mx: 1, whiteSpace: 'nowrap' }}>
      Sign Up
    </Button>
  );
};

const SlowLinearProgress = styled(LinearProgress)({
  '& .MuiLinearProgress-bar': {
    // apply a new animation-duration to the `.bar` class
    animationDuration: '8s',
  },
});
